import { useCallback, useState } from 'react';

import type { EventName } from '../../../utils/mixpanel';
import { mixpanelEvents } from '../../../utils/mixpanel';
import { VideaNotesUIState } from '../types';

interface UseVideaNotesEventHandlersParams {
  // State setters
  setAppointmentDataForVideaNotes: (data: any) => void;
  setUiStatus: (status: VideaNotesUIState) => void;
  setIsSheetOpen: (open: boolean) => void;
  setIsCancelRecordingConfirmationShown: (shown: boolean) => void;

  // Current state
  uiStatus: VideaNotesUIState;
  isCancelRecordingConfirmationShown: boolean;

  // Patient/appointment data
  patient: any;
  appointmentStartsAt: string | undefined;
  estimatedVisitId: number | string | null | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  username: string;

  // Actions from other hooks
  trackVideaNotesEvent: (
    event: EventName,
    payload?: Record<string, unknown>,
  ) => void;
  uploadAudio: (overrideBlobUrl?: string) => Promise<void>;
  triggerSaveNoteToPMS: () => void;
  triggerRetryWriteback: () => void;
  saveNoteBeforeClose: () => void;
}

export const useVideaNotesEventHandlers = ({
  setAppointmentDataForVideaNotes,
  setUiStatus,
  setIsSheetOpen,
  setIsCancelRecordingConfirmationShown,
  uiStatus,
  isCancelRecordingConfirmationShown,
  patient,
  appointmentStartsAt,
  estimatedVisitId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  username,
  trackVideaNotesEvent,
  uploadAudio,
  triggerSaveNoteToPMS,
  triggerRetryWriteback,
  saveNoteBeforeClose,
}: UseVideaNotesEventHandlersParams) => {
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  // Sheet open event
  const handleSheetOpen = useCallback(
    (e: Event) => {
      e.preventDefault();
      trackVideaNotesEvent(mixpanelEvents.videaNotesOpenSheet);
    },
    [trackVideaNotesEvent],
  );

  // Patient created in quick note form
  const handlePatientCreated = useCallback(
    (visitData: {
      id: string;
      firstName: string;
      lastName: string | null;
      sourcePatientId: string | null;
      dateOfBirth: Date | null;
      appointmentDate: Date;
      appointmentStartTime: string;
    }) => {
      const appointmentData = {
        appointmentStartsAt: new Date(visitData.appointmentDate).toISOString(),
        patient: {
          firstName: visitData.firstName,
          lastName: visitData.lastName ?? undefined,
          displayName: `${visitData.firstName} ${visitData.lastName ?? ''}`,
          sourcePatientId: visitData.sourcePatientId ?? undefined,
        },
        estimatedVisitId: null,
        standalonePatientVisitId: visitData.id,
        unifiedId: undefined,
        loggedInUser: username ?? '',
      };

      setAppointmentDataForVideaNotes(appointmentData);
      setUiStatus(VideaNotesUIState.AUDIO_CAPTURE);
    },
    [setAppointmentDataForVideaNotes, setUiStatus, username],
  );

  // Generate note from recording
  const handleGenerateNote = useCallback(
    (uploadedFile?: File, blobUrl?: string) => {
      trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesClick);
      setUiStatus(VideaNotesUIState.NOTE_GENERATION);
      uploadAudio(blobUrl);
    },
    [trackVideaNotesEvent, setUiStatus, uploadAudio],
  );

  // Create additional note for same patient
  const handleCreateAdditionalNote = useCallback(() => {
    setAppointmentDataForVideaNotes(null);
    setAppointmentDataForVideaNotes({
      appointmentStartsAt,
      patient,
      estimatedVisitId,
      unifiedId: unifiedAppointmentId,
      standalonePatientVisitId,
      loggedInUser: username,
    });
    setUiStatus(VideaNotesUIState.AUDIO_CAPTURE);
  }, [
    setAppointmentDataForVideaNotes,
    appointmentStartsAt,
    patient,
    estimatedVisitId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    username,
    setUiStatus,
  ]);

  // Recording controls
  const handleCancelRecording = useCallback(() => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesCancelRecorderClick);
  }, [trackVideaNotesEvent]);

  const handleCancelRecordingConfirmation = useCallback(() => {
    trackVideaNotesEvent(
      mixpanelEvents.videaNotesCancelRecorderConfirmationClick,
    );
  }, [trackVideaNotesEvent]);

  const handlePause = useCallback(() => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesPauseClick);
  }, [trackVideaNotesEvent]);

  const handleResume = useCallback(() => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesResumeClick);
  }, [trackVideaNotesEvent]);

  // PMS save actions
  const handleSaveNoteToPMS = useCallback(() => {
    triggerSaveNoteToPMS();
    trackVideaNotesEvent(mixpanelEvents.videaNotesWritebackClick);
  }, [triggerSaveNoteToPMS, trackVideaNotesEvent]);

  const handleRetryWritebackToPMS = useCallback(() => {
    triggerRetryWriteback();
    trackVideaNotesEvent(mixpanelEvents.videaNotesWritebackClick);
  }, [triggerRetryWriteback, trackVideaNotesEvent]);

  // Note interaction
  const handleSummaryTextAreaClick = useCallback(() => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesTextAreaClick);
  }, [trackVideaNotesEvent]);

  const handleCopyText = useCallback(
    (copiedText: string) => {
      navigator.clipboard.writeText(copiedText);
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultCopy);

      setShowCopyTooltip(true);
      setTimeout(() => {
        setShowCopyTooltip(false);
      }, 2000);
    },
    [trackVideaNotesEvent],
  );

  // Sheet close (used for both normal close and error dismiss)
  const handleCloseSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, [setIsSheetOpen]);

  // Sheet open change (handles cleanup on close)
  const handleSheetOpenChange = useCallback(() => {
    saveNoteBeforeClose();
    setIsSheetOpen(false);

    if (isCancelRecordingConfirmationShown) {
      setIsCancelRecordingConfirmationShown(false);
    }

    if (uiStatus === VideaNotesUIState.AUDIO_CAPTURE) {
      trackVideaNotesEvent(mixpanelEvents.videaNotesRecorderClose);
    } else if (uiStatus === VideaNotesUIState.SUMMARY_RESULT) {
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultClose);
    }
  }, [
    uiStatus,
    isCancelRecordingConfirmationShown,
    saveNoteBeforeClose,
    setIsSheetOpen,
    setIsCancelRecordingConfirmationShown,
    trackVideaNotesEvent,
  ]);

  return {
    // State
    showCopyTooltip,

    // Handlers
    handleSheetOpen,
    handlePatientCreated,
    handleGenerateNote,
    handleCreateAdditionalNote,
    handleCancelRecording,
    handleCancelRecordingConfirmation,
    handlePause,
    handleResume,
    handleSaveNoteToPMS,
    handleRetryWritebackToPMS,
    handleSummaryTextAreaClick,
    handleCopyText,
    handleCloseSheet,
    handleSheetOpenChange,
  };
};
